import type { FlagAction } from "@mnk/shared/models/flagAction";
import type { FlagColumn } from "@mnk/shared/models/flagColumn";
import { useEffect, useRef } from "react";
import { A4_HEIGHT, A4_WIDTH } from "../const";
import { formatValue, getPrecision } from "./unitPrecision";

const ROW_HEIGHT = 58;
const MARGIN_X = 250;
const MARGIN_Y = 150;
const HEADER_HEIGHT = 80;
const TITLE_FONT_SIZE = 56;
const FONT_SIZE = 32;
const COL_L1_WIDTH = 400;
const COL_L2_WIDTH = 1200;
const COL_UNIT_WIDTH = 270;
const COL_QTY_WIDTH = 300;
const COL_MOD_WIDTH = 300;

const USABLE_HEIGHT = 2100 - MARGIN_Y - 120; // 下余白120
export const ROWS_PER_PAGE = Math.floor((USABLE_HEIGHT - HEADER_HEIGHT) / ROW_HEIGHT);

interface Props {
  pageIndex: number;
  flagColumns: FlagColumn[];
  flagActions: FlagAction[];
  onCapture: (pageIndex: number, dataURL: string) => void;
}

export const SummaryPage = ({ pageIndex, flagColumns, flagActions, onCapture }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const W = A4_WIDTH;
    const tableWidth = W - MARGIN_X * 2;
    const colWidths = [COL_L1_WIDTH, COL_L2_WIDTH, COL_UNIT_WIDTH, COL_QTY_WIDTH, COL_MOD_WIDTH];
    const tableX = MARGIN_X;
    const tableY = MARGIN_Y;
    const totalTableHeight = HEADER_HEIGHT + ROW_HEIGHT * flagColumns.length;

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, W, A4_HEIGHT);

    ctx.fillStyle = "#111111";
    ctx.strokeStyle = "#111111";
    ctx.lineWidth = 2;

    // タイトル
    ctx.font = `${TITLE_FONT_SIZE}px sans-serif`;
    ctx.textAlign = "center";
    const titleY = MARGIN_Y - 40;
    ctx.fillText("数　量　表", W / 2, titleY);
    const titleWidth = ctx.measureText("数　量　表").width;
    ctx.beginPath();
    ctx.moveTo(W / 2 - titleWidth / 2, titleY + 8);
    ctx.lineTo(W / 2 + titleWidth / 2, titleY + 8);
    ctx.stroke();
    ctx.textAlign = "left";

    ctx.font = `${FONT_SIZE}px sans-serif`;

    ctx.fillStyle = "#111111";

    const headerTextY = tableY + HEADER_HEIGHT / 2 + FONT_SIZE / 3;
    const mergedWidth = COL_L1_WIDTH + COL_L2_WIDTH;
    const mergedX = tableX + mergedWidth;
    const unitX = mergedX + COL_UNIT_WIDTH;
    const qtyX = unitX + COL_QTY_WIDTH;

    ctx.textAlign = "center";
    ctx.fillText("単　価　項　目", tableX + mergedWidth / 2, headerTextY);
    ctx.fillText("単　位", mergedX + COL_UNIT_WIDTH / 2, headerTextY);
    ctx.fillText("数　量", unitX + COL_QTY_WIDTH / 2, headerTextY);
    ctx.fillText("変　更", qtyX + COL_MOD_WIDTH / 2, headerTextY);
    ctx.textAlign = "left";

    // データ行
    for (let i = 0; i < flagColumns.length; i++) {
      const col = flagColumns[i];
      const rowY = tableY + HEADER_HEIGHT + i * ROW_HEIGHT;
      const textY = rowY + ROW_HEIGHT / 2 + FONT_SIZE / 3;

      const precision = getPrecision(col.unit);
      const actions = flagActions.filter((a) => a.flagColumnId === col.id);
      const totalQty = actions.reduce((sum, a) => {
        const num = Number.parseFloat(a.value ?? "0");
        return sum + (Number.isNaN(num) ? 0 : num);
      }, 0);
      const totalMod = actions.reduce((sum, a) => {
        const num = Number.parseFloat(a.modifiedValue ?? "0");
        return sum + (Number.isNaN(num) ? 0 : num);
      }, 0);

      const l2Text = [col.formalNameL2, col.name].filter(Boolean).join(" ");
      const cells = [col.formalNameL1 ?? "", l2Text, col.unit ?? "", formatValue(totalQty, precision), formatValue(totalMod, precision)];

      let x = tableX;
      for (let j = 0; j < cells.length; j++) {
        if (j === 3 || j === 4) {
          ctx.textAlign = "right";
          ctx.fillText(cells[j], x + colWidths[j] - 8, textY);
          ctx.textAlign = "left";
        } else {
          ctx.fillText(cells[j], x + 8, textY);
        }
        x += colWidths[j];
      }
    }

    // 外枠
    ctx.strokeRect(tableX, tableY, tableWidth, totalTableHeight);

    // ヘッダー下線
    ctx.beginPath();
    ctx.moveTo(tableX, tableY + HEADER_HEIGHT);
    ctx.lineTo(tableX + tableWidth, tableY + HEADER_HEIGHT);
    ctx.stroke();

    // 行間の横線
    for (let i = 1; i < flagColumns.length; i++) {
      const lineY = tableY + HEADER_HEIGHT + i * ROW_HEIGHT;
      ctx.beginPath();
      ctx.moveTo(tableX, lineY);
      ctx.lineTo(tableX + tableWidth, lineY);
      ctx.stroke();
    }

    // 縦線: L1|L2 の区切りはヘッダーをスキップ（結合セル）
    const dataTop = tableY + HEADER_HEIGHT;
    ctx.beginPath();
    ctx.moveTo(tableX + COL_L1_WIDTH, dataTop);
    ctx.lineTo(tableX + COL_L1_WIDTH, tableY + totalTableHeight);
    ctx.stroke();

    // 単価項目|単位 の縦線はヘッダーから通す
    ctx.beginPath();
    ctx.moveTo(mergedX, tableY);
    ctx.lineTo(mergedX, tableY + totalTableHeight);
    ctx.stroke();

    // 単位|数量 の縦線
    ctx.beginPath();
    ctx.moveTo(unitX, tableY);
    ctx.lineTo(unitX, tableY + totalTableHeight);
    ctx.stroke();

    // 数量|変更 の縦線
    ctx.beginPath();
    ctx.moveTo(qtyX, tableY);
    ctx.lineTo(qtyX, tableY + totalTableHeight);
    ctx.stroke();

    onCapture(pageIndex, canvas.toDataURL("image/png"));
  }, [flagColumns, flagActions, pageIndex, onCapture]);

  return <canvas ref={canvasRef} width={A4_WIDTH} height={A4_HEIGHT} style={{ display: "none" }} />;
};
